import { useState, useEffect, useRef, useCallback } from 'react';
import { getFileType } from '../utils/photoHelpers';
import api, { guestApiPath } from '../../../api';
import { getGuestAuthToken } from '../../../utils/guestToken';

const REFRESH_INTERVAL_MS = 60 * 1000;
const EXPIRY_SAFETY_MARGIN_MS = 30 * 1000;

function isStreamFresh(streamData, now) {
  return Boolean(streamData)
    && typeof streamData.expiresAt === 'number'
    && streamData.expiresAt - EXPIRY_SAFETY_MARGIN_MS > now;
}

/**
 * Custom hook for managing video players and stream data
 * @param {Array} videoFiles - Array of video filenames to manage
 * @param {boolean} isActive - Whether the video manager is active
 * @param {string} galleryId - Resolved gallery tenant id (explicit, not shared storage)
 * @returns {Object} Video management functions and data
 */
export const useVideoManager = (videoFiles = [], isActive = true, galleryId = '') => {
  // eslint-disable-next-line no-unused-vars
  const [videoPlayers, setVideoPlayers] = useState(new Map());
  const [videoStreamData, setVideoStreamData] = useState(new Map());
  const fetchingVideosRef = useRef(new Set()); // Track videos currently being fetched
  const [refreshTick, setRefreshTick] = useState(0);

  // Periodically re-evaluate stream expiry while active so stale signed URLs
  // are refreshed before they are reused.
  useEffect(() => {
    if (!isActive) return undefined;
    const interval = setInterval(() => setRefreshTick((tick) => tick + 1), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isActive]);

  // Fetch video stream URLs when videos are provided
  useEffect(() => {
    if (!isActive || !videoFiles.length) return;

    const fetchVideoStreams = async () => {
      // Detect mobile device once for all videos
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      // Get device memory if available (for advanced optimizations)
      const deviceMemory = navigator.deviceMemory || null;

      // Get network type if available
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const networkType = connection ? connection.effectiveType : null;

      for (const filename of videoFiles) {
        const existing = videoStreamData.get(filename);
        if (isStreamFresh(existing, Date.now()) || fetchingVideosRef.current.has(filename)) {
          continue;
        }
        if (getFileType(filename) !== 'video') continue;

        fetchingVideosRef.current.add(filename); // Mark as being fetched

        try {
          const token = getGuestAuthToken();

          // Skip if no token - user needs to re-authenticate
          if (!token) {
            console.warn(`useVideoManager: No auth token available for ${filename}, skipping`);
            fetchingVideosRef.current.delete(filename);
            continue;
          }

          const response = await api.post(`${guestApiPath('/video-stream', galleryId)}`, {
            fileName: filename,
            quality: 'high',
            mobile: isMobile,
            deviceMemory,
            networkType,
          }, {
            headers: { Authorization: `Bearer ${token}` },
          });

          setVideoStreamData(prev => new Map(prev.set(filename, response.data)));
        } catch (error) {
          // Only 401 is treated as auth error (token invalid/expired)
          // 403/404/500 are server-side issues (file not found, permissions, etc.)
          if (error.response?.status === 401) {
            console.warn(`useVideoManager: Auth error for ${filename} - token expired or invalid`);
            // Auth issue will be handled by global interceptor in api.js
          } else if (error.response?.status === 404) {
            console.warn(`useVideoManager: Video not found: ${filename}`);
          } else if (error.response?.status === 403) {
            console.warn(`useVideoManager: Permission denied for ${filename}`);
          } else {
            console.warn(`useVideoManager: Failed to fetch video stream for ${filename}:`, error.message || error);
          }
        } finally {
          fetchingVideosRef.current.delete(filename); // Remove from fetching set
        }
      }
    };

    fetchVideoStreams();

    // Cleanup function to clear fetching state when videos change
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      fetchingVideosRef.current.clear();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, videoFiles, galleryId, refreshTick]); // Intentionally excluding videoStreamData to prevent infinite loop

  // Cleanup function for Plyr players
  const cleanupPlayers = useCallback(() => {
    setVideoPlayers(prevPlayers => {
      prevPlayers.forEach((player) => {
        if (player && typeof player.destroy === 'function') {
          player.destroy();
        }
      });
      return new Map();
    });
  }, []);

  // Get stream data for a specific filename. Expired entries are treated as
  // unavailable so the player never reuses a dead signed URL during refresh.
  const getStreamData = useCallback((filename) => {
    const streamData = videoStreamData.get(filename);
    return isStreamFresh(streamData, Date.now()) ? streamData : undefined;
  }, [videoStreamData]);

  // Check if stream data is loading (or expired, which is equivalent here)
  const isStreamLoading = useCallback((filename) => {
    return !isStreamFresh(videoStreamData.get(filename), Date.now());
  }, [videoStreamData]);

  return {
    setVideoPlayers,
    getStreamData,
    isStreamLoading,
    cleanupPlayers
  };
};
